import React, { createContext, useContext, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { filter } from 'lodash';
import { useTAApi } from 'modules/codeless-events/providers/TAApiProvider';
import { CONVERSION_CATEGORY } from 'modules/onboarding/OnboardingJourneyForm/components/conversions/consts/marketingObjectivesConfig';
import { useTrackingApi } from 'services/api';

const FetchedConversionsContext = createContext(undefined);

export const FetchedConversionsProvider = ({ children }) => {
    const { accountId } = useTAApi();
    const { getConversionsList } = useTrackingApi();
    const HOUR = 60 * 60 * 1000;

    const [hasFetched, setHasFetched] = useState(false);

    const {
        isLoading: isLoadingConversions,
        data: fetchedConversions,
        isSuccess: queryIsSuccess,
    } = useQuery(
        ['fetchedConversionsList', accountId],
        async () => {
            if (!accountId) return [];
            const data = await getConversionsList(accountId);
            return Array.isArray(data?.results) ? data.results : [];
        },
        {
            enabled: !!accountId,
            staleTime: HOUR,
            refetchOnWindowFocus: false,
            onSuccess: () => {
                setHasFetched(true);
            },
        }
    );

    const value = useMemo(
        () => ({
            isLoadingConversions,
            conversionsFetchSuccess: queryIsSuccess && hasFetched && !!fetchedConversions,
            unfilteredFetchedConversions: fetchedConversions,
            conversionsResult:
                filter(
                    fetchedConversions,
                    ({ category, isActive }) => isActive && category !== CONVERSION_CATEGORY.ENGAGEMENT
                ) ?? [],
        }),
        [isLoadingConversions, queryIsSuccess, hasFetched, fetchedConversions]
    );

    if (isLoadingConversions && !hasFetched) {
        return null;
    }

    return <FetchedConversionsContext.Provider value={value}>{children}</FetchedConversionsContext.Provider>;
};

export const useFetchedConversionsContext = () => {
    const context = useContext(FetchedConversionsContext);
    if (context === undefined) {
        throw new Error('useFetchedConversionsContext must be used within a FetchedConversionsProvider');
    }
    return context;
};
